import { z } from 'zod';
import { FleetAccountAuthSchema, FleetAccountQuotaSchema, FLEET_ACCOUNT_LIMITS } from './fleet-status.schemas';

/**
 * The fleet's registry of engine accounts (fleet-agent ADR 0027). A worker owns its accounts and their logins; the
 * backend keeps one row per engine + e-mail + machine, with the operator's settings for it, and never a token. Rows
 * come from the tick's `accounts` (an account with no e-mail cannot be registered); the latest reading and auth
 * state stay where the tick already stores them, on the machine.
 */

/** A fraction of a usage window, 0–1. */
const FractionSchema = z.number().min(0).max(1);

/**
 * How the page reads an account, first that applies winning: the worker cannot log in (`reauth`), the operator
 * turned it off (`paused`), it is at a limit or at its `maxWeekly` (`limited`), else `ready`.
 */
export const FLEET_REGISTERED_ACCOUNT_STATES = ['reauth', 'paused', 'limited', 'ready'] as const;
export type FleetRegisteredAccountState = (typeof FLEET_REGISTERED_ACCOUNT_STATES)[number];

export const FleetRegisteredAccountPathParamsSchema = z.object({ accountId: z.uuid() });
export type FleetRegisteredAccountPathParams = z.infer<typeof FleetRegisteredAccountPathParamsSchema>;

/** The operator's settings for one account, as the tick hands them back to the worker that holds it. */
export const FleetAccountSettingsSchema = z.object({
  engine: z.string().min(1).max(FLEET_ACCOUNT_LIMITS.engine),
  email: z.string().min(1).max(FLEET_ACCOUNT_LIMITS.email),
  /** `false`: the worker leaves this account out of its rotation. */
  enabled: z.boolean(),
  /** The share of the weekly window the worker may use this account up to, 0–1. */
  maxWeekly: FractionSchema,
  /**
   * What the fleet's OTHER approved machines last reported of the same login (engine + e-mail), so a host learns an
   * account is spent without hitting the 429 itself. Never the ticking machine's own report. Absent from a backend
   * that does not compute it.
   */
  shared: z
    .object({
      /** The reading with the latest `readAt`; `null` when no other machine has one. */
      quota: FleetAccountQuotaSchema.nullable(),
      /** The latest `limitedUntil` still in the future; `null` when no other machine reports one. */
      limitedUntil: z.iso.datetime().nullable(),
    })
    .nullish(),
});
export type FleetAccountSettings = z.infer<typeof FleetAccountSettingsSchema>;

/** One registered account as the operator sees it: whose worker holds it, its last reading and its settings. */
export const FleetRegisteredAccountViewSchema = z.object({
  id: z.uuid(),
  machine: z.object({ id: z.uuid(), name: z.string() }),
  engine: z.string(),
  email: z.string(),
  /** Home-relative, as the worker reported it; `null` is the engine's default login. */
  configDir: z.string().nullable(),
  /** The lanes the worker runs on this account. */
  lanes: z.array(z.string()),
  auth: FleetAccountAuthSchema,
  quota: FleetAccountQuotaSchema.nullable(),
  /** When the holder's last tick said the account takes work again; `null` not spent, absent from an older agent. */
  limitedUntil: z.iso.datetime().nullish(),
  enabled: z.boolean(),
  maxWeekly: FractionSchema,
  state: z.enum(FLEET_REGISTERED_ACCOUNT_STATES),
  /** The last time the worker's tick listed this account. */
  reportedAt: z.iso.datetime(),
});
export type FleetRegisteredAccountView = z.infer<typeof FleetRegisteredAccountViewSchema>;

export const ListFleetRegisteredAccountsResponseSchema = z.object({
  data: z.array(FleetRegisteredAccountViewSchema),
});
export type ListFleetRegisteredAccountsResponse = z.infer<typeof ListFleetRegisteredAccountsResponseSchema>;

/** Either or both; an empty body changes nothing. */
export const UpdateFleetRegisteredAccountBodySchema = z.object({
  enabled: z.boolean().optional(),
  maxWeekly: FractionSchema.optional(),
});
export type UpdateFleetRegisteredAccountBody = z.infer<typeof UpdateFleetRegisteredAccountBodySchema>;
